'use client';

import React, { FC, useState, ChangeEvent } from 'react';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import styles from './ThemeArchive.module.scss';
import Layout from '@/src/components/layout/Layout';
import Breadcrumbs from '@/src/components/ui/breadcrumbs/Breadcrumbs';
import ThemesList from '@/src/components/ui/themesList/ThemesList';
import { ITheme } from '@/src/lib/types/themeInterface';
import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import classNames from 'classnames';
import { TSelect, getFilteredData } from '@/src/lib/helpers/getFilteredData';
import CollabSection from '@/src/components/sections/collabSection/CollabSection';
import SimpleSubscribe from '@/src/components/sections/simpleSubscribe/SimpleSubscribe';

type TThemesData = {
  themesData: ITheme[];
};

const ThemeArchive: FC<TThemesData> = ({ themesData }) => {
  const [activeTag, setActiveTag] = useState('All Categories');

  const [activeOption, setActiveOption] = useState<TSelect>('popularity');

  const handleOptionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setActiveOption(e.target.value as TSelect);
  };

  const filtersArr = themesData
    .map((item) => item.demoList)
    .flat()
    .map((item) => item?.demoTag);
  const filters = [...new Set(filtersArr)];

  const handleFilters = (filter: string) => {
    setActiveTag(filter);
  };

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  const heading = 'Diverse Templates to Shape Your Online Store';
  const excerpt = `Each design presents a unique style to suit varied tastes, products, and brand personalities. Choose what’s best for you.`;

  return (
    <Layout>
      <Breadcrumbs />
      <Wrapper>
        <div ref={ref} className={styles.info}>
          <h1 className={`heading__1 ${styles.heading} ${inView && styles.animate__heading}`}>
            {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
          </h1>
          <p className={`excerpt ${styles.excerpt} ${inView && styles.animate__excerpt}`}>
            {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
          </p>
          <div>
            <div className={styles.filter__bars}>
              <ul className={styles.filters}>
                <li
                  key={`All Categories`}
                  className={`${styles.filter} ${
                    activeTag === 'All Categories' && styles['active']
                  }`}
                  onClick={() => handleFilters('All Categories')}>
                  All Categories
                </li>
                {filters.map((filter, i) => (
                  <li
                    key={`${filter}-${i + 1}`}
                    className={`${styles.filter} ${activeTag === filter && styles['active']}`}
                    onClick={() => handleFilters(filter!)}>
                    {filter}
                  </li>
                ))}
              </ul>

              <div className={styles.select__wrapper}>
                <span>Order by:</span>
                <select
                  id="select-filter"
                  className={classNames(styles.input, styles.select)}
                  aria-label="Filter"
                  value={activeOption}
                  onChange={handleOptionChange}>
                  <option value="popularity">Popularity</option>
                  <option value="latest">Latest first</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <ThemesList
          themesData={getFilteredData(themesData, activeOption)}
          activeTag={activeTag}
          handleFilters={handleFilters}
        />
      </Wrapper>
      <CollabSection />
      <SimpleSubscribe />
    </Layout>
  );
};

export default ThemeArchive;
